import { MapPin, Check, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import SEO, { localBusinessSchema, serviceSchema, breadcrumbSchema } from "../components/SEO";
import { SectionLabel, Breadcrumbs, CTASection } from "../components/Shared";
import { LeadForm } from "../components/LeadForm";
import { IMAGES, BRAND } from "../data/siteData";

export default function LocationPage({ loc }) {
  const crumbs = [{ name: "Home", path: "/" }, { name: "Service Areas", path: "/" }, { name: loc.city, path: `/${loc.slug}` }];
  return (
    <>
      <SEO
        title={loc.metaTitle}
        description={loc.metaDescription}
        path={`/${loc.slug}`}
        ogImage={IMAGES.house1}
        schemas={[localBusinessSchema([loc.city]), serviceSchema("Garage Floor Coating", loc.city), breadcrumbSchema(crumbs)]}
      />

      <section className="hero-gradient py-20 md:py-28 px-6 md:px-12 grain relative" data-testid="location-hero">
        <div className="max-w-7xl mx-auto">
          <Breadcrumbs items={crumbs} />
          <SectionLabel>Serving {loc.city}, Arizona</SectionLabel>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white max-w-4xl">{loc.h1}</h1>
          <p className="text-gray-300 text-base md:text-lg leading-relaxed mt-6 max-w-2xl">{loc.intro}</p>
          <div className="flex flex-col sm:flex-row gap-4 mt-9">
            <Link to="/free-estimate" data-testid="location-hero-cta" className="bg-[#028090] text-white hover:bg-[#026c7a] transition-colors duration-300 px-8 py-4 uppercase font-bold tracking-wider text-center">
              Free {loc.city} Estimate
            </Link>
            <a href={BRAND.phoneHref} data-testid="location-hero-phone" className="border border-white/30 text-white hover:border-[#028090] hover:text-[#028090] transition-colors duration-300 px-8 py-4 uppercase font-bold tracking-wider text-center">
              Call {BRAND.phone}
            </a>
          </div>
        </div>
      </section>

      <section className="bg-white py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[1.2fr_1fr] gap-12 lg:gap-16">
          <div>
            <SectionLabel>Local Expertise</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#000000] mb-6">
              TRI-BOND™ Garage Floors, Built for {loc.city}
            </h2>
            {loc.body.split("\n\n").map((para, i) => (
              <p key={i} className="text-gray-600 leading-relaxed mb-5">{para}</p>
            ))}
            <h3 className="font-display text-xl font-bold text-[#000000] mt-10 mb-4">Neighborhoods we serve in {loc.city}</h3>
            <div className="flex flex-wrap gap-2.5" data-testid="location-neighborhoods">
              {loc.neighborhoods.map((n) => (
                <span key={n} className="flex items-center gap-1.5 bg-gray-100 text-gray-700 text-sm px-3.5 py-2">
                  <MapPin size={13} className="text-[#028090]" strokeWidth={1.5} /> {n}
                </span>
              ))}
            </div>
            <div className="mt-10 space-y-3">
              {["One-day install — park on it in 24 hours", "100% UV-stable — never yellows in Arizona sun", "Lifetime limited warranty in writing", "Licensed & insured, locally owned"].map((b) => (
                <p key={b} className="flex items-start gap-2.5 text-gray-700 text-sm"><Check size={17} className="text-[#028090] mt-0.5 shrink-0" /> {b}</p>
              ))}
            </div>
            <div className="flex flex-wrap gap-5 mt-10 text-sm">
              <Link to="/services" data-testid="location-services-link" className="text-[#028090] font-bold uppercase tracking-wider inline-flex items-center gap-2 hover:gap-3 transition-all">Explore the TRI-BOND™ System <ArrowRight size={15} /></Link>
              <Link to="/" data-testid="location-home-link" className="text-[#028090] font-bold uppercase tracking-wider inline-flex items-center gap-2 hover:gap-3 transition-all">Back to Homepage <ArrowRight size={15} /></Link>
            </div>
          </div>
          <div>
            <div className="bg-[#000000] p-8 md:p-10 teal-glow">
              <h3 className="font-display text-2xl font-bold text-white mb-2">Get Your Free {loc.city} Estimate</h3>
              <p className="text-gray-400 text-sm mb-6">Fixed written pricing. Flake samples in hand. Zero pressure.</p>
              <LeadForm dark sourceTag={loc.city} />
            </div>
            <div className="mt-8 border border-gray-200">
              <iframe
                title={`Map of ${loc.city}, AZ`}
                src={`https://www.google.com/maps?q=${encodeURIComponent(loc.city + ", AZ")}&output=embed`}
                className="w-full h-72"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="location-map"
              />
            </div>
          </div>
        </div>
      </section>

      <CTASection title={`The Best Garage Floor in ${loc.city} Starts With a Free Estimate`} />
    </>
  );
}
